import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getTechnology, getTechnologyContent } from '../data/technologies';
import { setPageMeta, plainTextForMeta } from '../utils/setPageMeta';

const publicUrl = process.env.PUBLIC_URL || '';

// Custom hook for scroll animations
function useScrollAnimation(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return { ref, isVisible };
}

// Custom Cursor Component
function CustomCursor() {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      
      const target = e.target as HTMLElement;
      const isInteractive = target.closest('a, button');
      setIsHovering(!!isInteractive);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      <div 
        className={`custom-cursor ${isHovering ? 'hovering' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
      <div 
        className={`custom-cursor-dot ${isHovering ? 'hovering' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
    </>
  );
}

export default function Technology() {
  const { slug } = useParams<{ slug: string }>();
  const tech = getTechnology(slug ?? '');
  const content = getTechnologyContent(slug ?? '');
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const articleSection = useScrollAnimation(0.2);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = (window.scrollY / documentHeight) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (tech && content) {
      setPageMeta({
        title: `${tech.name} — development expertise | LeadingNG`,
        description: plainTextForMeta(content.intro),
        canonicalPath: `/technology/${tech.slug}`,
        keywords: `${tech.name}, LeadingNG, software development, Web3, blockchain, consulting, Auckland New Zealand`,
      });
    } else if (slug) {
      setPageMeta({
        title: 'Technology page not found | LeadingNG',
        description:
          'That technology page is not available. Explore LeadingNG for Web3, Solana, Ethereum, Rust, TypeScript, React and cloud engineering.',
        canonicalPath: `/technology/${slug}`,
        keywords: 'LeadingNG, software development, technology',
      });
    }
  }, [tech, content, slug]);

  if (!tech || !content) {
    return (
      <section className="section">
        <div className="container text-center">
          <h1 className="title">Technology not found</h1>
          <p className="intro">We don't have a page for this technology yet.</p>
          <Link to="/" className="btn btn-cta btn-cta-secondary">Back to Home</Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <CustomCursor />
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} aria-hidden="true" />

      <section className="page-hero">
        <div className="hero-bg" style={{ transform: `translateY(${scrollY * 0.5}px)` }} />
        <div className="container text-center">
          <span className="page-hero-label animate-fade-in">Technology</span>
          <h1 className="page-title animate-slide-up" style={{ animationDelay: '0.1s' }}>{tech.name}</h1>
          <p className="page-intro animate-fade-in" style={{ animationDelay: '0.3s' }}>
            {content.intro}
            <br /><br />
            <Link to="/contact-us" className="btn btn-cta btn-cta-primary magnetic-btn">
              <span className="btn-text">Talk to us</span>
              <span className="btn-bg" />
            </Link>
          </p>
        </div>
      </section>

      <section ref={articleSection.ref} className={`section tech-article ${articleSection.isVisible ? 'visible' : ''}`}>
        <div className="container">
          <div className="tech-article-inner">
            <img
              src={`${publicUrl}/assets/images/technologies/${tech.image}`}
              alt={tech.name}
              className="tech-article-logo scroll-reveal"
            />
            <div className="tech-article-content">
              {content.paragraphs.map((html, i) => (
                <div
                  key={i}
                  className="content-section scroll-reveal"
                  style={{ transitionDelay: `${0.2 + i * 0.15}s` }}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        /* Custom Cursor */
        .custom-cursor,
        .custom-cursor-dot {
          position: fixed;
          pointer-events: none;
          z-index: 10000;
          transform: translate(-50%, -50%);
        }
        .custom-cursor {
          width: 40px;
          height: 40px;
          border: 2px solid var(--color-primary);
          border-radius: 50%;
          transition: width 0.3s ease, height 0.3s ease, border-color 0.3s ease;
          opacity: 0.5;
        }
        .custom-cursor.hovering {
          width: 60px;
          height: 60px;
          border-color: var(--color-secondary);
          opacity: 0.8;
        }
        .custom-cursor-dot {
          width: 6px;
          height: 6px;
          background: var(--color-primary);
          border-radius: 50%;
          transition: transform 0.15s ease, background 0.3s ease;
        }
        .custom-cursor-dot.hovering {
          transform: translate(-50%, -50%) scale(1.5);
          background: var(--color-secondary);
        }

        /* Scroll Progress Bar */
        .scroll-progress-bar {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          z-index: 9999;
          transition: width 0.1s ease-out;
          box-shadow: 0 0 10px var(--color-primary);
        }

        /* Hero animations */
        .animate-fade-in {
          opacity: 0;
          animation: fadeIn 1s ease-out forwards;
        }
        .animate-slide-up {
          opacity: 0;
          transform: translateY(30px);
          animation: slideUp 0.8s ease-out forwards;
        }
        @keyframes fadeIn {
          to { opacity: 1; }
        }
        @keyframes slideUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Scroll reveal animations */
        .scroll-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .visible .scroll-reveal {
          opacity: 1;
          transform: translateY(0);
        }

        /* Magnetic Button Effect */
        .magnetic-btn {
          position: relative;
          overflow: hidden;
          transform-style: preserve-3d;
          transition: transform 0.3s ease;
        }
        .magnetic-btn .btn-text {
          position: relative;
          z-index: 2;
        }
        .magnetic-btn .btn-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, rgba(255,255,255,0.2), transparent);
          transform: scale(0);
          transition: transform 0.5s ease;
          z-index: 1;
        }
        .magnetic-btn:hover .btn-bg {
          transform: scale(2.5);
        }
        .magnetic-btn::after {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, 
            transparent, 
            rgba(255,255,255,0.1), 
            transparent);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
        }
        .magnetic-btn:hover::after {
          transform: translateX(100%);
        }
        .magnetic-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        }

        .page-hero {
          position: relative;
          padding: 88px 20px 64px;
          overflow: hidden;
          color: #fff;
        }
        .hero-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(160deg, var(--color-bg-dark) 0%, #1a252f 100%);
          z-index: 0;
        }
        .page-hero > .container {
          position: relative;
          z-index: 1;
        }
        .page-hero-label {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--color-primary);
          margin-bottom: 8px;
        }
        .page-hero a { color: #fff; }
        .page-hero .btn-cta-primary:hover { color: #fff; }
        .page-title { margin: 0 0 12px; font-size: 2rem; font-weight: 700; letter-spacing: -0.02em; }
        .page-intro { margin: 0; opacity: 0.92; font-size: 1.05rem; }
        .tech-article { 
          background: #fff;
          position: relative;
          overflow: hidden;
        }
        .tech-article::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300a0e3' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.3;
          z-index: 0;
        }
        .tech-article > .container {
          position: relative;
          z-index: 1;
        }
        .tech-article-inner {
          max-width: 720px;
          margin: 0 auto;
        }
        .tech-article-logo {
          float: left;
          margin: 0 28px 20px 0;
          max-width: 100px;
          height: auto;
          filter: grayscale(0.5);
          transition: filter 0.3s ease, transform 0.3s ease;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .tech-article-logo:hover {
          filter: grayscale(0);
          transform: scale(1.05) rotate(-2deg);
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
        }
        .tech-article-content { 
          overflow: hidden;
        }
        .content-section { 
          margin-bottom: 24px;
          position: relative;
          padding-left: 20px;
        }
        .content-section::before {
          content: '';
          position: absolute;
          left: 0;
          top: 6px;
          width: 4px;
          height: calc(100% - 12px);
          background: linear-gradient(180deg, var(--color-primary), var(--color-secondary));
          border-radius: 2px;
          opacity: 0;
          transform: scaleY(0);
          transition: opacity 0.5s ease, transform 0.5s ease;
          transform-origin: top;
        }
        .visible .content-section::before {
          opacity: 0.3;
          transform: scaleY(1);
        }
        .content-section p { 
          margin: 0 0 14px; 
          color: var(--color-text-muted); 
          line-height: 1.75;
        }
        .content-section strong {
          color: var(--color-primary);
          font-weight: 600;
        }

        /* Reduced motion preferences */
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in,
          .animate-slide-up,
          .scroll-reveal {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
          .custom-cursor,
          .custom-cursor-dot {
            display: none;
          }
          .scroll-progress-bar {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .custom-cursor,
          .custom-cursor-dot {
            display: none;
          }
          .scroll-progress-bar {
            height: 2px;
          }
        }

        @media (max-width: 600px) {
          .tech-article-logo { 
            float: none; 
            display: block; 
            margin: 0 auto 20px;
          }
          .content-section {
            padding-left: 16px;
          }
        }
      `}</style>
    </>
  );
}
